'use client';

import { cn } from '../../../utils/cn';
import type { TableCardSkeletonProps } from './types';

// INNER row heights: the bordered row card adds 1px top + bottom, so the
// outer block totals the designed 68px / 80px.
const ROW_HEIGHT_DESKTOP = 'h-[66px] md:h-[78px]';
/**
 * A `compact` row's floor. Compact rows are content-sized (`py-2`), but every
 * row in one table renders the same cell shapes, so pinning a minimum makes the
 * body's height a function of the ROW COUNT alone. Hosts do NOT reserve
 * `rows × 56` themselves any more: `skeletonRows` + `minRows` hold
 * a full page's height in every state (placeholders below), and a cell taller
 * than this floor (avatar + two lines) wants a non-compact, fixed-height row.
 */
const COMPACT_ROW_MIN_HEIGHT = 'min-h-[56px]';
const ROW_HEIGHT_MOBILE = 'h-[66px]';

/** @deprecated Use `DataTableSkeleton` from `data-table` instead. */
export function TableCardSkeleton({
  columns,
  rows = 10,
  compact = false,
  hasActions = false,
  hasChevron = false,
  className,
  rowClassName,
}: TableCardSkeletonProps) {
  // The multi-line placeholder belongs to the primary text column — the wide,
  // GROWING one (`flex-1` / `flex-[n]`), not necessarily the first column, which
  // may be a narrow leading action/icon column. Falls back to the first column.
  const primaryKey = (columns.find(c => /flex-(1|\[)/.test(c.width || '')) ?? columns[0])?.key;
  return (
    <>
      {Array.from({ length: rows }).map((_, index) => (
        <div
          key={index}
          className={cn(
            'relative animate-pulse overflow-hidden rounded-[6px] border border-ods-border bg-ods-card',
            className,
          )}
        >
          {/* Desktop Skeleton */}
          {/* Matches the ROW it stands in for: a compact row is content-sized
              with a floor, a normal row is a fixed height. Using the taller of
              the two here made every compact table jump on its first paint. */}
          <div
            className={cn(
              'hidden items-center gap-4 px-4 md:flex',
              compact ? cn('py-2', COMPACT_ROW_MIN_HEIGHT) : cn('py-0', ROW_HEIGHT_DESKTOP),
              rowClassName,
            )}
          >
            {columns.map(column => (
              <div key={column.key} className={cn('flex shrink-0 flex-col justify-center', column.width || 'flex-1')}>
                <div className="mb-1 h-5 w-3/4 rounded bg-ods-bg-surface" />
                {/* second line simulates multi-line content on the primary column */}
                {index % 2 === 0 && column.key === primaryKey && (
                  <div className="h-4 w-1/2 rounded bg-ods-bg-surface opacity-60" />
                )}
              </div>
            ))}

            {/* Actions skeleton */}
            {hasActions && (
              <div className={cn('flex shrink-0 items-center gap-2', !hasChevron && 'ml-auto')}>
                <div className="h-12 w-12 rounded bg-ods-bg-surface" />
                <div className="h-12 w-24 rounded bg-ods-bg-surface" />
              </div>
            )}

            {/* Chevron skeleton */}
            {hasChevron && (
              <div className={cn('flex w-12 shrink-0 items-center justify-end', !hasActions && 'ml-auto')}>
                <div className="h-8 w-8 rounded bg-ods-bg-surface" />
              </div>
            )}
          </div>

          {/* Mobile Skeleton */}
          <div
            className={cn(
              'flex items-center justify-start gap-3 px-3 md:hidden',
              // Mirror the real row on mobile too: a compact row is
              // content-sized with a floor, a normal row a fixed height.
              // Hard-wiring ROW_HEIGHT_MOBILE made every COMPACT table jump
              // 68px -> 58px per row when the data replaced the skeleton.
              compact ? cn('py-2', COMPACT_ROW_MIN_HEIGHT) : cn('py-0', ROW_HEIGHT_MOBILE),
              rowClassName,
            )}
          >
            {/* The inner padding is the NON-compact row's; a compact row is
                sized by its own `py-2` + floor, so adding `py-3` here made the
                compact mobile skeleton 78px against a real row's 58. */}
            <div className={cn('flex min-w-0 flex-1 flex-col justify-center', compact ? 'py-0' : 'py-3')}>
              <div className="mb-2 h-4 w-3/4 rounded bg-ods-bg-surface" />
              <div className="h-3 w-1/2 rounded bg-ods-bg-surface opacity-60" />
            </div>
            {hasActions && <div className="h-12 w-12 shrink-0 rounded bg-ods-bg-surface" />}
            {hasChevron && <div className="h-8 w-8 shrink-0 rounded bg-ods-bg-surface" />}
          </div>
        </div>
      ))}
    </>
  );
}

/** @deprecated */
export { COMPACT_ROW_MIN_HEIGHT, ROW_HEIGHT_DESKTOP, ROW_HEIGHT_MOBILE };
